import { useState, type FormEvent } from 'react';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';

export function Contact() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSending(true);
    setError(false);

    const formData = new FormData(event.currentTarget);
    formData.append('access_key', import.meta.env.VITE_WEB3FORMS_ACCESS_KEY);
    formData.append('subject', 'New contact form submission — Northforge site');

    try {
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();

      if (response.ok && result.success) {
        setSent(true);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden px-6 pb-[88px] pt-24">
      <div
        className="pointer-events-none absolute -bottom-[220px] left-1/2 h-[640px] w-[640px] -translate-x-1/2 animate-[nfs-pulse_11s_ease-in-out_infinite] rounded-full"
        style={{ background: 'radial-gradient(circle, var(--glow), transparent 70%)' }}
      />

      <div className="relative mx-auto max-w-[760px] text-center">
        <span className="text-xs tracking-[0.3em] text-accent">04 — CONTACT</span>
        <h2
          data-reveal
          className="m-0 mb-4 mt-3.5 font-display text-[clamp(30px,3.8vw,48px)] font-semibold tracking-[-0.02em]"
        >
          Tell us your biggest time-waster.
        </h2>
        <p data-reveal className="mx-auto mb-[38px] max-w-[520px] text-[16.5px] leading-[1.6] text-dim">
          One question: what's the process costing you the most time right now?
        </p>

        <div
          data-reveal
          className="rounded-[20px] border border-[var(--line)] bg-[var(--card)] p-6 text-left transition-colors duration-[450ms]"
        >
          {sent ? (
            <div className="px-2.5 py-[34px] text-center">
              <div className="mx-auto mb-4 flex h-[46px] w-[46px] items-center justify-center rounded-full bg-accent text-xl text-white">
                ✓
              </div>
              <h3 className="m-0 mb-2 font-display text-xl font-semibold">
                Thanks — that's the one we'll look at.
              </h3>
              <p className="m-0 text-[15px] text-dim">
                We'll come back to you within one working day.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-3.5">
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                aria-hidden="true"
                autoComplete="off"
                className="hidden"
              />
              <div className="grid grid-cols-1 gap-3.5 min-[900px]:grid-cols-2">
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  required
                  className="rounded-xl border border-[var(--line)] bg-transparent px-4 py-3.5 text-[15px] outline-none transition-colors focus:border-accent"
                />
                <input
                  type="text"
                  name="contact"
                  placeholder="Email or WhatsApp number"
                  required
                  className="rounded-xl border border-[var(--line)] bg-transparent px-4 py-3.5 text-[15px] outline-none transition-colors focus:border-accent"
                />
              </div>
              <textarea
                name="message"
                rows={4}
                placeholder="What's the process that's costing you the most time right now?"
                required
                className="resize-y rounded-xl border border-[var(--line)] bg-transparent px-4 py-3.5 font-body text-[15px] outline-none transition-colors focus:border-accent"
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="submit"
                  disabled={sending}
                  className="rounded-full bg-accent px-[26px] py-3.5 text-[15.5px] font-medium text-white shadow-[0_14px_30px_-16px_rgba(208,119,24,0.9)] transition-colors hover:bg-accent-cta-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? 'Sending…' : 'Send it over'}
                </button>
                <span className="text-[13.5px] text-dim">
                  Or email{' '}
                  <a href="mailto:hello@northforgesolutions.co.za">
                    hello@northforgesolutions.co.za
                  </a>
                </span>
              </div>
              {error && (
                <p className="m-0 text-[13.5px] text-red-500">
                  Something went wrong sending that — please try again, or use the email above.
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
